import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, RefreshCw, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { TECH_INTERESTS, TRIGGER_OPTIONS } from "@/data/content";
import { getAvailability, submitBooking, isBusinessEmail } from "@/lib/leads";

const ConsultationContext = createContext({ open: () => {} });
export const useConsultation = () => useContext(ConsultationContext);

const EMPTY = {
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  phone: "",
  industry: "",
  interest: "",
  trigger: "",
  slot: "",
  sameDay: false,
};

const formatSlotLabel = (slot) => {
  if (slot.label) return slot.label;
  const date = new Date(slot.start);
  return date.toLocaleDateString([], { weekday: "short" }) + " - " + date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export const ConsultationProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [availabilityLoaded, setAvailabilityLoaded] = useState(false);
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
  const [website, setWebsite] = useState("");

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const selectedSlot = slots.find((slot) => slot.start === form.slot);

  const refreshAvailability = useCallback((preferredSlot = "", refresh = 0) => {
    setSlotsError("");
    setSlotsLoading(true);

    getAvailability(refresh)
      .then((availableSlots) => {
        const slotStillAvailable = availableSlots.some((slot) => slot.start === preferredSlot);
        setSlots(availableSlots);
        if (!slotStillAvailable) set("slot", "");
      })
      .catch(() => setSlotsError("Calendar availability is temporarily unavailable."))
      .finally(() => {
        setAvailabilityLoaded(true);
        setSlotsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!isOpen || availabilityLoaded || slotsLoading) return;
    refreshAvailability();
  }, [availabilityLoaded, isOpen, refreshAvailability, slotsLoading]);

  const open = useCallback(() => {
    setDone(false);
    setForm(EMPTY);
    setErrors({});
    setSubmitting(false);
    setSlots([]);
    setSlotsError("");
    setAvailabilityLoaded(false);
    setFormStartedAt(Date.now());
    setWebsite("");
    setIsOpen(true);
  }, []);

  const validate = () => {
    const nextErrors = {};
    if (form.firstName.trim().length < 2) nextErrors.firstName = "Enter a first name.";
    if (form.lastName.trim().length < 2) nextErrors.lastName = "Enter a last name.";
    if (form.company.trim().length < 2) nextErrors.company = "Enter a company name.";
    if (!isBusinessEmail(form.email)) nextErrors.email = "Enter a valid business email.";
    if (form.phone.trim().length < 7) nextErrors.phone = "Enter a phone number.";
    if (form.industry.trim().length < 2) nextErrors.industry = "Enter your industry.";
    if (!form.interest) nextErrors.interest = "Choose a primary interest.";
    if (!form.trigger) nextErrors.trigger = "Choose what triggered this.";
    if (!form.sameDay && !selectedSlot) nextErrors.slot = "Pick an available time slot.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const name = `${form.firstName} ${form.lastName}`.trim();
    setSubmitting(true);
    try {
      await submitBooking({
        name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        industry: form.industry,
        interest: form.interest,
        trigger: form.trigger,
        slot_start: form.sameDay ? null : selectedSlot.start,
        slot_end: form.sameDay ? null : selectedSlot.end,
        slot_label: form.sameDay ? "Same-day request (+$100)" : selectedSlot.label,
        same_day_requested: form.sameDay,
        same_day_fee: form.sameDay ? "$100" : null,
        website,
        _formStartedAt: formStartedAt,
      });
      setDone(true);
      toast.success(form.sameDay ? "Same-day request sent" : "Consultation booked", {
        description: form.sameDay ? "We will follow up to confirm today's opening." : "Calendar invite and confirmation are on the way.",
      });
    } catch (error) {
      toast.error("We couldn't book that time.", { description: "Please refresh availability or email us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConsultationContext.Provider value={{ open }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-border/80 bg-card p-0 text-card-foreground shadow-[var(--shadow-elegant)] sm:max-w-3xl">
          {!done ? (
            <form onSubmit={submit} className="p-6 sm:p-8">
              <div className="sr-only" aria-hidden="true">
                <Label htmlFor="consultation-website">Website</Label>
                <Input
                  id="consultation-website"
                  name="website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="mb-7">
                <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 font-mono text-xs text-primary">
                  <ShieldCheck className="h-3.5 w-3.5" /> 25-Minute Honest Consultation
                </div>
                <h2 className="font-display text-3xl font-semibold text-foreground sm:text-4xl">No pitch. Just answers.</h2>
                <p className="mt-3 max-w-2xl text-base text-muted-foreground">
                  Tell us where the pressure is and we will pair you with the right technology advisor.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
                <Field label="First name *" error={errors.firstName}>
                  <Input value={form.firstName} onChange={(event) => set("firstName", event.target.value)} placeholder="Jane" />
                </Field>
                <Field label="Last name *" error={errors.lastName}>
                  <Input value={form.lastName} onChange={(event) => set("lastName", event.target.value)} placeholder="Rivera" />
                </Field>
                <Field label="Business Email *" error={errors.email}>
                  <Input type="email" value={form.email} onChange={(event) => set("email", event.target.value)} placeholder="jane@company.com" />
                </Field>
                <Field label="Phone *" error={errors.phone}>
                  <Input type="tel" value={form.phone} onChange={(event) => set("phone", event.target.value)} placeholder="(555) 123-4567" />
                </Field>
                <Field label="Company Name *" error={errors.company}>
                  <Input value={form.company} onChange={(event) => set("company", event.target.value)} placeholder="Acme Corp" />
                </Field>
                <Field label="Industry *" error={errors.industry}>
                  <Input value={form.industry} onChange={(event) => set("industry", event.target.value)} placeholder="e.g., Healthcare, Financial Services" />
                </Field>
                <Field label="Primary interest *" error={errors.interest}>
                  <Select value={form.interest} onValueChange={(value) => set("interest", value)}>
                    <SelectTrigger className="h-12 rounded-md border-border bg-secondary/60">
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {TECH_INTERESTS.map((interest) => (
                        <SelectItem key={interest} value={interest}>{interest}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="What triggered this? *" error={errors.trigger}>
                  <Select value={form.trigger} onValueChange={(value) => set("trigger", value)}>
                    <SelectTrigger className="h-12 rounded-md border-border bg-secondary/60">
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {TRIGGER_OPTIONS.map((trigger) => (
                        <SelectItem key={trigger} value={trigger}>{trigger}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <Label className="text-sm text-foreground/90">Pick a time slot *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => refreshAvailability(form.slot, Date.now())}
                    disabled={slotsLoading}
                    className="h-8 gap-1.5 px-2.5 text-xs"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${slotsLoading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>

                {slotsLoading && (
                  <div className="rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
                    Loading live availability...
                  </div>
                )}

                {slotsError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {slotsError}
                  </div>
                )}

                {!slotsLoading && !slotsError && slots.length > 0 && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {slots.map((slot) => (
                      <button
                        type="button"
                        key={slot.start}
                        onClick={() => set("slot", slot.start)}
                        className={`min-h-12 rounded-md border px-4 py-3 text-sm font-medium transition-colors ${
                          form.slot === slot.start
                            ? "border-primary bg-primary/15 text-foreground shadow-[var(--shadow-glow)]"
                            : "border-border bg-secondary/40 text-muted-foreground hover:border-primary/45 hover:text-foreground"
                        }`}
                      >
                        {formatSlotLabel(slot)}
                      </button>
                    ))}
                  </div>
                )}

                {!slotsLoading && !slotsError && slots.length === 0 && (
                  <div className="rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
                    No consultation times are available right now. Please refresh or contact us directly.
                  </div>
                )}

                {errors.slot && <p className="mt-2 text-xs text-destructive">{errors.slot}</p>}
              </div>

              <div
                className={`mt-5 rounded-md border p-3 transition-colors ${
                  form.sameDay ? "border-primary bg-primary/10" : "border-border bg-secondary/30"
                }`}
              >
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={form.sameDay}
                    onCheckedChange={(checked) => {
                      set("sameDay", checked === true);
                      if (checked) set("slot", "");
                    }}
                    className="mt-0.5"
                  />
                  <span className="space-y-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <Zap className="h-3.5 w-3.5 text-primary" /> Request same-day technology help
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Need to talk today? Add the $100 same-day request and we will confirm the earliest opening.
                    </span>
                  </span>
                </label>
              </div>

              <Button type="submit" variant="hero" size="lg" className="mt-6 w-full" disabled={submitting || (!form.sameDay && (slotsLoading || !selectedSlot))}>
                {submitting ? "Confirming..." : form.sameDay ? "Request Same-Day Consultation" : "Confirm My Consultation"}
              </Button>

              <p className="mt-4 flex items-center justify-center gap-1.5 text-center font-mono text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Supplier-neutral. Zero obligation.
              </p>
            </form>
          ) : (
            <div className="p-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
                <CheckCircle2 className="h-8 w-8 text-success" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold">You are on the calendar</h3>
              <p className="mx-auto mt-2 max-w-sm text-muted-foreground">
                {form.sameDay ? (
                  <>
                    We received your same-day request with the $100 priority fee noted. We will follow up at{" "}
                    <span className="font-medium text-foreground">{form.email}</span> to confirm today's opening.
                  </>
                ) : (
                  <>
                    We reserved <span className="font-medium text-foreground">{selectedSlot?.label || form.slot}</span>. A calendar invite
                    and confirmation email will be sent to <span className="font-medium text-foreground">{form.email}</span>.
                  </>
                )}
              </p>
              <Button className="mt-6" variant="outline" onClick={() => setIsOpen(false)}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ConsultationContext.Provider>
  );
};

const Field = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm text-foreground/90">{label}</Label>
    {children}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);
